/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file mailCollabUtils.js
 * @description
 * Pure helpers for Happy Mailer shared-mailbox collaboration (assign / team tags / replied-by /
 * snooze). Free of React and network code so they are unit-testable. 1:1 port of the admin
 * `components/mail/collab/mailCollabUtils.js` (minus web-only URL helpers), with a translator-based
 * relative-time formatter because Hermes ships no reliable `Intl.RelativeTimeFormat`.
 */

/**
 * Smart views offered for every shared mailbox. `mine_unassigned` ("Mine + unassigned") is the
 * default of a shared mailbox; a specific teammate is picked separately (`{address, assignee}`).
 */
export const COLLAB_VIEWS = ['mine_unassigned', 'mine', 'unassigned', 'open', 'done'];

/** Smart view applied to a shared mailbox until the user picks another one. */
export const DEFAULT_COLLAB_VIEW = 'mine_unassigned';

/** Snooze presets resolved server-side (see backend `resolveSnoozePreset`). */
export const SNOOZE_PRESETS = ['later_today', 'tomorrow', 'next_week', 'weekend'];

/** Auto-assign modes accepted by `PATCH /mail/collab/mailboxes/:address`. */
export const AUTO_ASSIGN_MODES = ['off', 'round_robin', 'least_busy', 'ai'];

/** Palette offered when creating a tag. */
export const TAG_COLORS = ['#F97316', '#2563eb', '#16a34a', '#d97706', '#dc2626', '#db2777', '#7c3aed', '#0891b2'];

/** Push / notification event keys introduced by shared-mailbox collaboration. */
export const COLLAB_EVENT_KEYS = [
    'mail.assigned',
    'mail.reassigned',
    'mail.shared_reply',
    'mail.colleague_activity',
    'mail.snooze_due',
    // Private thread discussion (shared mailboxes): comment / @mention.
    'mail.thread_comment',
    'mail.thread_mention',
];

const lower = (value) => String(value || '').trim().toLowerCase();

/**
 * Whether a push / notification payload belongs to a collaboration event.
 *
 * @param {Object} data Notification `data` / `metadata` payload.
 * @returns {boolean} True for one of {@link COLLAB_EVENT_KEYS}.
 */
export const isCollabEvent = (data) => COLLAB_EVENT_KEYS.includes(lower(data?.eventKey || data?.eventkey));

/**
 * Address a mail account is known by in the collaboration backend (mirrors the backend
 * `accountAddress`: first e-mail-shaped value of `senderEmail`, `oauth.email`, `incoming.username`,
 * `incoming.user`).
 *
 * @param {Object} account Mailbox account from the mail settings.
 * @returns {string} Lower-cased address ('' when unknown).
 */
export const getAccountAddress = (account) => {
    const candidates = [account?.senderEmail, account?.oauth?.email, account?.incoming?.username, account?.incoming?.user];

    for (const candidate of candidates) {
        const value = lower(typeof candidate === 'string' ? candidate : '');

        if (value.includes('@')) {
            return value;
        }
    }

    return '';
};

/**
 * Resolves the mailbox address a list row / message belongs to.
 *
 * @param {Object} mail Normalised mail message (`mailboxAccountId`).
 * @param {Object[]} accounts Accounts of the mail settings.
 * @returns {string} Lower-cased address ('' when the account cannot be resolved).
 */
export const resolveMailAddress = (mail, accounts = []) => {
    const accountId = mail?.mailboxAccountId;

    if (!accountId) {
        return '';
    }

    return getAccountAddress((accounts || []).find((account) => (account.id || account._id) === accountId));
};

/**
 * Mailbox address of a whole thread: the latest message's account, else the first message of the
 * thread whose `mailboxAccountId` resolves. Never falls back to the active / default account, so a
 * thread opened from a second shared mailbox keeps its own collaboration context.
 *
 * @param {Object|null} latestMessage Latest real message.
 * @param {Object[]} [messages] Every real message of the thread.
 * @param {Object[]} [accounts] Accounts of the mail settings.
 * @returns {string} Lower-cased address ('' when unresolved).
 */
export const resolveThreadAddress = (latestMessage, messages = [], accounts = []) => {
    const direct = resolveMailAddress(latestMessage, accounts);

    if (direct) {
        return direct;
    }
    for (const msg of Array.isArray(messages) ? messages : []) {
        const address = resolveMailAddress(msg, accounts);

        if (address) {
            return address;
        }
    }

    return '';
};

/**
 * Conversation key of a thread: the latest message's key, else the first message carrying one
 * (older rows may expose it on the detail message only).
 *
 * @param {Object|null} latestMessage Latest real message.
 * @param {Object[]} [messages] Every real message of the thread.
 * @returns {string} Lower-cased key or ''.
 */
export const resolveThreadConversationKey = (latestMessage, messages = []) => {
    const direct = getConversationKey(latestMessage);

    if (direct) {
        return direct;
    }
    for (const msg of Array.isArray(messages) ? messages : []) {
        const key = getConversationKey(msg);

        if (key) {
            return key;
        }
    }

    return '';
};

/**
 * Finds the id of the caller's own account for a mailbox address (used to scope the list).
 *
 * @param {string} address Mailbox address.
 * @param {Object[]} accounts Accounts of the mail settings.
 * @returns {string} Account id ('' when none).
 */
export const findAccountIdForAddress = (address, accounts = []) => {
    const wanted = lower(address);
    const match = (accounts || []).find((account) => account.enabled !== false && getAccountAddress(account) === wanted);

    return match ? (match.id || match._id || '') : '';
};

/**
 * Conversation key of a message (the backend lower-cases it; absent on pre-feature rows).
 *
 * @param {Object} mail Normalised mail message.
 * @returns {string} Key or '' when missing.
 */
export const getConversationKey = (mail) => lower(mail?.conversationKey);

/**
 * `/collab/threads/assigned` query for a smart view or team tag.
 *
 * @param {{address: string, view?: string, tag?: string}} view Active collaboration view.
 * @returns {Object} Query params.
 */
export const buildAssignedQuery = (view) => {
    if (view.tag) {
        return {address: view.address, tag: view.tag, status: 'open'};
    }
    if (view.assignee) {
        return {address: view.address, assignee: view.assignee, status: 'open'};
    }
    if (view.view === 'mine_unassigned') {
        // Hide only the threads that belong to somebody else (done ones included).
        return {address: view.address, assignee: 'others'};
    }
    if (view.view === 'mine') {
        return {address: view.address, assignee: 'me', status: 'open'};
    }
    if (view.view === 'unassigned') {
        return {address: view.address, assignee: 'unassigned', status: 'open'};
    }
    if (view.view === 'done') {
        return {address: view.address, status: 'done'};
    }

    // "All open": every thread that is NOT done -> fetch the done keys and exclude them.
    return {address: view.address, status: 'done'};
};

/**
 * Normalises the `/collab/threads/assigned` result for a view into include/exclude semantics.
 *
 * @param {{view?: string, tag?: string}} view Active view.
 * @param {{mode?: string, conversationKeys?: string[], truncated?: boolean}} result API result.
 * @returns {{mode: 'include'|'exclude', keys: Set<string>, truncated: boolean}} Filter.
 */
export const toViewFilter = (view, result = {}) => ({
    // The "All open" view reuses the done keys, inverted; "Mine + unassigned" hides foreign threads.
    mode: !view.tag && !view.assignee && (view.view === 'open' || view.view === 'mine_unassigned')
        ? 'exclude'
        : (result.mode === 'exclude' ? 'exclude' : 'include'),
    keys: new Set((result.conversationKeys || []).map(lower)),
    truncated: Boolean(result.truncated),
});

/**
 * Collaboration view that really drives the list: the explicitly picked one, else - while the list
 * is scoped to ONE shared mailbox - that mailbox's default "Mine + unassigned" view (`implicit`).
 *
 * @param {Object|null} picked View picked by the user (`null` = none).
 * @param {string} accountId Account the list is scoped to ('' / null = all inboxes).
 * @param {Object[]} accounts Accounts of the mail settings.
 * @param {(address: string) => (Object|null)} getSharedMailbox Store lookup of a shared mailbox.
 * @returns {Object|null} Effective view.
 */
export const resolveEffectiveView = (picked, accountId, accounts = [], getSharedMailbox = () => null) => {
    if (picked || !accountId) {
        return picked || null;
    }
    const account = (accounts || []).find((entry) => (entry.id || entry._id) === accountId);
    const address = getAccountAddress(account);

    return address && getSharedMailbox(address)
        ? {address, view: DEFAULT_COLLAB_VIEW, implicit: true}
        : null;
};

/**
 * Applies a view filter to list rows. Rows without a conversation key are kept for exclude
 * filters (nothing is known about them) and dropped for include filters.
 *
 * @param {Object[]} items Rows.
 * @param {{mode: string, keys: Set<string>}|null} filter View filter.
 * @returns {Object[]} Filtered rows.
 */
export const applyViewFilter = (items, filter) => {
    if (!filter) {
        return items;
    }

    return (items || []).filter((mail) => {
        const key = getConversationKey(mail);

        if (filter.mode === 'exclude') {
            return !key || !filter.keys.has(key);
        }

        return Boolean(key) && filter.keys.has(key);
    });
};

/**
 * Index over pending snoozes for O(1) row lookups.
 *
 * @param {Object[]} snoozes Pending snoozes (`GET /mail/snoozes?status=pending`).
 * @param {number} [now] Reference time (ms) - snoozes already past `until` no longer hide rows.
 * @returns {{byKey: Map<string, Object>, byMessage: Map<string, Object>}} Index.
 */
export const buildSnoozeIndex = (snoozes, now = Date.now()) => {
    const byKey = new Map();
    const byMessage = new Map();

    (Array.isArray(snoozes) ? snoozes : []).forEach((snooze) => {
        if (!snooze || (snooze.status && snooze.status !== 'pending')) {
            return;
        }
        if (snooze.until && new Date(snooze.until).getTime() <= now) {
            return;
        }
        if (snooze.conversationKey) {
            byKey.set(lower(snooze.conversationKey), snooze);
        }
        if (snooze.messageId) {
            byMessage.set(String(snooze.messageId), snooze);
        }
    });

    return {byKey, byMessage};
};

/**
 * Pending snooze of a list row, if any.
 *
 * @param {Object} mail Row.
 * @param {{byKey: Map, byMessage: Map}} index Snooze index.
 * @returns {Object|null} Snooze.
 */
export const findSnooze = (mail, index) => {
    if (!mail || !index) {
        return null;
    }

    return index.byKey.get(getConversationKey(mail))
        || index.byMessage.get(String(mail.id || mail._id || ''))
        || null;
};

/**
 * Hides snoozed conversations from a list (they reappear once `until` passes / `mail:snooze_due`).
 *
 * @param {Object[]} items Rows.
 * @param {{byKey: Map, byMessage: Map}} index Snooze index.
 * @param {string} [keepId] Row that must stay visible (the open message).
 * @returns {Object[]} Visible rows.
 */
export const hideSnoozed = (items, index, keepId = '') => {
    if (!index || (!index.byKey.size && !index.byMessage.size)) {
        return items;
    }

    return (items || []).filter((mail) => (keepId && (mail.id || mail._id) === keepId) || !findSnooze(mail, index));
};

/**
 * Keeps only the rows that ARE snoozed (the "Snoozed" smart view).
 *
 * @param {Object[]} items Rows.
 * @param {{byKey: Map, byMessage: Map}} index Snooze index.
 * @returns {Object[]} Snoozed rows.
 */
export const onlySnoozed = (items, index) => (items || []).filter((mail) => Boolean(findSnooze(mail, index)));

/**
 * Compact localized relative time ("5 min ago") using the app translator, with an absolute date
 * fallback beyond 30 days. Keys: `mail_collab_time_now|min|hour|day`.
 *
 * @param {string|Date|number} value Timestamp.
 * @param {Function} t i18n translator.
 * @param {string} [locale] BCP-47 locale for the absolute fallback.
 * @param {number} [now] Reference time (ms).
 * @returns {string} Text ('' for an invalid date).
 */
export const formatRelativeTime = (value, t, locale = 'en', now = Date.now()) => {
    const time = new Date(value).getTime();

    if (!value || Number.isNaN(time)) {
        return '';
    }

    const seconds = Math.max(0, Math.round((now - time) / 1000));

    if (seconds < 60) {
        return t('mail_collab_time_now');
    }
    if (seconds < 3600) {
        return t('mail_collab_time_min', {count: Math.floor(seconds / 60)});
    }
    if (seconds < 86400) {
        return t('mail_collab_time_hour', {count: Math.floor(seconds / 3600)});
    }
    if (seconds < 86400 * 30) {
        return t('mail_collab_time_day', {count: Math.floor(seconds / 86400)});
    }

    try {
        return new Date(time).toLocaleDateString(locale);
    } catch (_e) {
        return new Date(time).toLocaleDateString();
    }
};

/**
 * Splits a mailbox's tags into the ones assigned to a thread state (unknown ids dropped).
 *
 * @param {Object[]} mailboxTags Tags `{id,name,color}` of the mailbox.
 * @param {string[]} tagIds Tag ids on the thread.
 * @returns {Object[]} Resolved tags in thread order.
 */
export const resolveTags = (mailboxTags, tagIds) => {
    const byId = new Map((mailboxTags || []).map((tag) => [tag.id, tag]));

    return (tagIds || []).map((id) => byId.get(id)).filter(Boolean);
};

/**
 * Stable id of a user-like object across the `_id` / `id` shapes.
 *
 * @param {Object} user User.
 * @returns {string} Id ('' when missing).
 */
export const userId = (user) => String(user?._id || user?.id || '');

/**
 * Localised sentence of one activity entry (the admin `MailCollabActivity.sentence` port).
 *
 * @param {Object} entry Activity entry `{type, user, toUser, meta}`.
 * @param {Function} t i18n translator.
 * @param {Object[]} [mailboxTags] Team tags of the mailbox (to name tag events).
 * @returns {string} Sentence.
 */
export const activitySentence = (entry, t, mailboxTags = []) => {
    const actor = entry?.user?.name || t('mail_collab_someone');
    const target = entry?.toUser?.name || '';
    const tagId = entry?.meta?.tagId;
    const tag = resolveTags(mailboxTags, [tagId])[0];
    const params = {actor, target, tag: tag?.name || tagId || ''};

    if (entry?.type === 'assigned' && entry.toUser && entry.user && userId(entry.toUser) === userId(entry.user)) {
        return t('mail_collab_act_claimed', params);
    }

    const known = ['assigned', 'reassigned', 'unassigned', 'replied', 'tagged', 'untagged', 'done', 'reopened', 'note', 'auto_assigned'];

    return known.includes(entry?.type)
        ? t(`mail_collab_act_${entry.type}`, params)
        : t('mail_collab_act_other', params);
};
